export enum DatasetType {
  Directory = 'directory',
  File = 'file',
  Dataframe = 'dataframe',
  Json = 'json',
  Unknown = 'unknown',
}

export enum InputSource {
  UserSpecified = 'user_specified',
  FromDataset = 'from_dataset',
  Both = 'both',
}

export enum SchemaValidationMode {
  Error = 'error',
  Warn = 'warn',
}

export enum OutputContractKind {
  Explicit = 'explicit',
  BuiltIn = 'built_in',
  Derived = 'derived',
  None = 'none',
  Incompatible = 'incompatible',
}

export type AutomationSchemaRef = {
  schema_id: string;
  version: number;
};

export type AutomationJsonSchema = {
  schema_id: string;
  version: number;
  description: string;
  json_schema: Record<string, unknown>;
  validation_mode: SchemaValidationMode;
};

// export type DataOutput = {
//   dataset_type?: DatasetType;
// };

export type StaticDataInputConfig = {
  static_dataset_id: string | null;
};

export type LinkedDataInputConfig = {
  linked_dataset_id: string | null;
};

export type DynamicDataInputConfig = {
  source_step_id: string | null;
  output_id: string | null;
};

export type EmptyDataInputConfig = {
  dataset_type: DatasetType;
};

export type LocalSubflowPortDataInputConfig = {
  port_id: string;
};

export enum DataInputType {
  Static = 'static',
  Linked = 'linked',
  Dynamic = 'dynamic',
  Empty = 'empty',
  LocalSubflowPort = 'local_subflow_port',
}

export type DataInputConfig =
  | StaticDataInputConfig
  | DynamicDataInputConfig
  | LinkedDataInputConfig
  | EmptyDataInputConfig
  | LocalSubflowPortDataInputConfig;

export type DataInput = {
  name: string | null;
  binding_name?: string | null;
  last_known_display_name: string | null;
  data_input_type: DataInputType;
  config: DataInputConfig;
};

export enum RuntimeOutputNameSource {
  Static = 'static',
  DataInput = 'data_input',
}

export type RuntimeOutputName = {
  source: RuntimeOutputNameSource;
  static_value: string;
  data_input: DataInput;
};

export type DynamicDataInput = {
  name: string | null;
  binding_name?: string | null;
  last_known_display_name: string | null;
  data_input_type: DataInputType.Dynamic;
  config: DynamicDataInputConfig;
};

export enum ExecutionType {
  Code = 'Code',
  AI = 'AI',
}

export enum TransformCodeSourceMode {
  GenerateAlways = 'generate_always',
  ReuseCachedCode = 'reuse_cached_code',
  ProvidedCode = 'provided_code',
  ProvidedCodeWithGeneratedFallback = 'provided_code_with_generated_fallback',
}

export function isProvidedCodeSourceMode(codeSourceMode: TransformCodeSourceMode): boolean {
  return [
    TransformCodeSourceMode.ProvidedCode,
    TransformCodeSourceMode.ProvidedCodeWithGeneratedFallback,
  ].includes(codeSourceMode);
}

export enum TransformCachedCodeReuseScope {
  WithinParentScope = 'within_parent_scope',
  AcrossRuns = 'across_runs',
}

export enum CodeStepExecutionCacheMode {
  Automatic = 'automatic',
  AlwaysFresh = 'always_fresh',
}

export enum TransformReviewMode {
  Semantic = 'semantic',
  SchemaAndReviewInstructions = 'schema_and_review_instructions',
  SchemaOnly = 'schema_only',
}

export enum ConditionAction {
  ChildSteps = 'child_steps',
  AskUser = 'ask_user',
  RaiseError = 'raise_error',
  ReturnDataset = 'return_dataset',
  Skip = 'skip',
}

export enum ExtractionFieldType {
  Text = 'Text',
  Number = 'Number',
  Boolean = 'True/False',
  Object = 'Object',
  List = 'List',
  // Code = 'Code',
}

/**
 * The persisted contract for what an optional extracted data field returns
 * when the source does not contain an answer. `default_value` below is kept
 * only so existing automations can continue to round-trip unchanged.
 */
export type MissingValue =
  | { kind: 'type_empty' }
  | { kind: 'null' }
  | { kind: 'value'; value: string | number | boolean | unknown[] | Record<string, unknown> };

export enum TableSourceType {
  SpreadsheetWorksheet = 'spreadsheet_worksheet',
  PdfTable = 'pdf_table',
}

export type SourceSelectorConfig = {
  source_type?: TableSourceType | null;
  parent_source?: string | null;
  container?: string | null;
  attributes?: Record<string, string>;
};

export type SourceRequirementConfig = {
  selectors?: SourceSelectorConfig | null;
  entity_overrides?: Record<string, SourceSelectorConfig>;
};

/**
 * A source-backed condition under which an optional field intentionally has
 * no value. This does not choose the value returned for ordinary absence.
 */
export type NotApplicablePolicy = {
  condition: string;
};

export type DataField = {
  field_id: string;
  name: string;
  field_type: ExtractionFieldType;
  is_required: boolean;
  default_value: string | null;
  missing_value?: MissingValue | null;
  description: string | null;
  formatting_rules: string | null;
  /**
   * Opt-in executable policy for numeric source cells that are genuinely
   * blank. Omitted configurations remain false for backwards compatibility.
   */
  blank_as_zero_allowed?: boolean;
  source_requirement?: SourceRequirementConfig | null;
  not_applicable_policy?: NotApplicablePolicy | null;
  dependencies: string[];
  internet_access: boolean;
};

export type InsightField = {
  field_id: string;
  name: string;
  description: string | null;
  infer_answer: boolean;
  internet_access: boolean;
};

export type DataframeIterationMetadata = {
  type: 'row' | 'column' | 'cell';
};

export type DirectoryIterationMetadata = {
  recursive: boolean;
};

export type JsonIterationMetadata = {
  key: string | null;
};

export type IterationConfigMetadata = {
  json_metadata: JsonIterationMetadata;
};

export enum FilterType {
  Files = 'Files',
  FileContents = 'FileContents',
}

export enum FilterMode {
  Condition = 'Condition',
  Selection = 'Selection',
}

export enum SpreadsheetFilterMode {
  TreatAsWorkbook = 'TreatAsWorkbook',
  TreatAsSeparateSheets = 'TreatAsSeparateSheets',
}

export enum FileCandidateScope {
  ParsedLeaves = 'parsed_leaves',
  RetainedFiles = 'retained_files',
}

export enum FileSelectionMode {
  NaturalLanguage = 'natural_language',
  DeterministicRelativePath = 'deterministic_relative_path',
  DeterministicFileId = 'deterministic_file_id',
  DeterministicSourceName = 'deterministic_source_name',
}

export enum StepType {
  Trigger = 'trigger',
  Dispatch = 'dispatch',
  ExtractData = 'extract_data',
  TransformData = 'transform_data',
  SubAutomation = 'sub_automation',
  RunMetadata = 'run_metadata',
  Iterate = 'iterate',
  Repeat = 'repeat',
  Retry = 'retry',
  FailureHandler = 'failure_handler',
  CreateDirectory = 'create_directory',
  GetFileFromDirectory = 'get_file_from_directory',
  Filter = 'filter',
  Conditional = 'condition',
  Validate = 'validate',
  HumanDecisionGate = 'human_decision_gate',
  LocalSubflowCall = 'local_subflow_call',
}

export enum StepCreationPreset {
  BrowserAutomation = 'web_browser',
  WebResearch = 'web_research',
  CreateCsvFile = 'create_csv_file',
  CreateExcelFile = 'create_excel_file',
  BuildExcelWorkbook = 'build_excel_workbook',
  CreateWordFile = 'generate_word_file',
  RunOptimizationSolver = 'run_optimization_solver',
  ExcelWorkbookToJson = 'excel_workbook_to_json',
}

export type AddableStepType = StepType | StepCreationPreset;

export type StepDataOutput = {
  step_id: string;
  step_type: StepType;
  step_name: string;
  output_name: string | null;
  output_id: string | null;
  is_scope_promoted?: boolean;
  // dataset_type: DatasetType;
};

// export type StepDataOutputs = PaginatedResponse<StepDataOutput>;

export type StepDataOutputs = {
  step_data_outputs: Record<string, StepDataOutput[]>;
};

export type OutputContract = {
  kind: OutputContractKind;
  // Older backend deployments did not include availability. Treat it as
  // guaranteed until the backend contract is available during a phased roll-out.
  availability?: OutputAvailability;
  dataset_type: DatasetType;
  schema_ref: AutomationSchemaRef | null;
  schema_id: string | null;
  version: number | null;
  schema_label: string;
  description: string;
  json_schema: Record<string, unknown> | null;
  validation_mode: SchemaValidationMode;
  artifact_kind?: 'immutable_file' | 'external_resource' | null;
  original_file_transport?: 'runtime_checked' | null;
  artifact_evidence_profile?: 'workbook' | 'blob_review' | 'external_resource' | null;
  expected_mime_types?: string[];
  source_step_id: string | null;
  source_output_id: string | null;
  diagnostics: string[];
  recursive_reference: boolean;
};

export enum OutputAvailability {
  Guaranteed = 'guaranteed',
  RuntimeOptional = 'runtime_optional',
}

export enum InputAvailabilityPolicy {
  PreserveSlots = 'preserve_slots',
  OmitUnavailable = 'omit_unavailable',
  RequireAvailable = 'require_available',
  PropagateUnavailable = 'propagate_unavailable',
  Fallback = 'fallback',
  OptionalTarget = 'optional_target',
}

export type StepOutputContract = {
  data_output: StepDataOutput;
  contract: OutputContract;
};

export type StepOutputContracts = {
  step_output_contracts: Record<string, StepOutputContract[]>;
  upstream_output_contracts: Record<string, StepOutputContract[]>;
  children_output_contracts: Record<string, StepOutputContract[]>;
  input_availability_policies?: Record<string, Record<string, InputAvailabilityPolicy>>;
};

export enum ConfigFieldErrorType {
  MissingRequiredValue = 'missing_required_value',
  InvalidValue = 'invalid_value',
  InvalidType = 'invalid_type',
  TextTooLong = 'text_too_long',
}

export type ConfigFieldError = {
  type: ConfigFieldErrorType;
  message: string;
};

export type ConfigFieldValue = {
  field_id: string;
  value: any | null;
  is_hidden: boolean;
  is_disabled: boolean;
  errors: ConfigFieldError[];
};

export type ConfigValues = {
  fields: ConfigFieldValue[];
};
